import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, Clock, MessageCircle, Phone, ChevronRight, ChevronLeft, CheckCircle2, Star, Lock, FileCheck2, Zap } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = '/speed-motors-logo.png';
const HERO_IMG = 'https://images.unsplash.com/photo-1557598628-bdd7d0767917?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

/* Paleta exclusiva do logo: azul-marinho + branco */
const NAVY = '#1B2668';
const NAVY_DARK = '#111A4A';
const NAVY_MID = '#27339B';

const DOWN_OPTIONS = ['$1.000', '$2.000', '$3.000', '$5.000+'];
const DOC_OPTIONS = ['Passaporte', 'ITIN', 'Social'];
const WHEN_OPTIONS = ['Esta semana', 'Este mês', 'Em 1–3 meses', 'Só pesquisando'];
const FIN_OPTIONS = ['Sim', 'Não'];
const CH_WA = 'WhatsApp';
const CH_CALL = 'Chamada telefônica';

const T = {
  pt: {
    badge: 'Aprovação com ITIN ou Passaporte', header_cta: 'Simular agora',
    kicker: 'Simulação em 60 segundos',
    title1: 'Financie seu carro nos EUA — ', title_hl: 'mesmo sem Social',
    sub: <>Preencha a simulação e receba na hora sua pré-análise de financiamento. Atendemos com <b>Passaporte, ITIN ou Social</b> — 100% em português.</>,
    bullets: ['Sem consulta que afete seu score', 'Resposta de um especialista em minutos', 'Entrada a partir de $1.000', 'Carros com garantia e inspeção completa'],
    proof1: '+1.200 brasileiros atendidos', proof2: 'em Massachusetts e região',
    form_title: 'Simule seu financiamento', form_sub: 'Grátis, rápido e sem compromisso.', step: 'PASSO', of: 'DE',
    l_nome: 'Nome completo', p_nome: 'Seu nome e sobrenome', l_tel: 'Telefone (com DDD)', l_email: 'E-mail',
    l_fin: 'Você já possui algum veículo financiado?', l_doc: 'Qual documento você tem?',
    l_down: 'Quanto pretende dar de entrada?', p_down: 'Ou digite outro valor ($)',
    l_when: 'Pretende comprar o veículo quando?', l_channel: 'Quer receber sua simulação agora por:',
    wa_desc: 'Receba a simulação direto no seu WhatsApp', call_label: 'Chamada telefônica', call_desc: 'Um especialista liga para você em português',
    privacy: 'Ao continuar você concorda em ser contatado pela Speed Motors. Sem spam, sem consulta que afete seu crédito.',
    next: 'Continuar', back: 'Voltar', submit: 'Ver minha simulação agora →',
    calc: ['Analisando seu perfil...', 'Consultando bancos parceiros...', 'Pré-aprovando sua taxa especial...'], calc_wait: 'Aguarde só um instante...',
    ok_title: 'Sua pré-análise está pronta! 🎉',
    ok_msg: (name, ch) => <>Recebemos seus dados, <b>{name}</b>. Um especialista Speed Motors vai te chamar <b>em até 10 minutos</b> por <b style={{ color: NAVY }}>{ch}</b> com a sua simulação personalizada.</>,
    protocol: 'Protocolo', entrada: 'Entrada', documento: 'Documento', ok_privacy: 'Seus dados estão protegidos e não afetam seu score.',
    trust_title: 'Por que financiar com a Speed Motors?',
    trust: [['Aceitamos seu documento', 'Passaporte, ITIN ou Social — trabalhamos com bancos que aprovam recém-chegados.'], ['Resposta em minutos', 'Nada de esperar dias. Um especialista te retorna em até 10 minutos no horário comercial.'], ['100% em português', 'Equipe brasileira em Massachusetts. Você entende cada detalhe do seu contrato.']],
    how_title: 'Como funciona',
    how: [['Preencha a simulação', 'Leva menos de 1 minuto — só os dados essenciais.'], ['Receba sua pré-análise', 'Nosso time analisa e monta as melhores opções para o seu perfil.'], ['Escolha seu carro', 'Um especialista te chama por WhatsApp ou telefone e você sai dirigindo.']],
    mid_cta: 'Quero minha simulação grátis', testi_title: 'Quem já saiu dirigindo',
    testimonials: [['Marcos A. — Everett, MA', 'Cheguei há 8 meses, só tinha passaporte. Em 2 dias estava com meu Corolla financiado. Atendimento nota 10 em português.'], ['Juliana R. — Framingham, MA', 'Fiz a simulação no Instagram e em 5 minutos me chamaram no WhatsApp. Super transparentes com as parcelas.'], ['Pedro S. — Worcester, MA', 'Consegui aprovar com ITIN e entrada de $2.000. Recomendo demais a Speed Motors.']],
    footer: 'Simulação gratuita e sem compromisso. A pré-análise não representa aprovação final de crédito, sujeita à avaliação dos bancos parceiros. Nenhuma consulta feita nesta página afeta seu score.',
    sticky: 'Simular meu financiamento grátis',
    e_nome: 'Digite seu nome completo.', e_tel: 'Digite um telefone válido com DDD.', e_email: 'Digite um e-mail válido.',
    e_fin: 'Informe se você já possui veículo financiado.', e_doc: 'Selecione qual documento você tem.', e_down: 'Informe quanto pretende dar de entrada.', e_when: 'Informe quando pretende comprar.', e_channel: 'Escolha como quer receber sua simulação.', e_send: 'Não foi possível enviar. Tente novamente.',
    na_title: 'Página não disponível', na_sub: 'Esta página não existe ou foi pausada.',
    opt: { 'Sim': 'Sim', 'Não': 'Não', 'Passaporte': 'Passaporte', 'ITIN': 'ITIN', 'Social': 'Social', 'Esta semana': 'Esta semana', 'Este mês': 'Este mês', 'Em 1–3 meses': 'Em 1–3 meses', 'Só pesquisando': 'Só pesquisando', [CH_WA]: 'WhatsApp', [CH_CALL]: 'Chamada telefônica' },
  },
  es: {
    badge: 'Aprobación con ITIN o Pasaporte', header_cta: 'Simular ahora',
    kicker: 'Simulación en 60 segundos',
    title1: 'Financia tu auto en USA — ', title_hl: 'incluso sin Social',
    sub: <>Completa la simulación y recibe al instante tu preanálisis de financiamiento. Atendemos con <b>Pasaporte, ITIN o Social</b> — 100% en español.</>,
    bullets: ['Sin consultas que afecten tu score', 'Respuesta de un especialista en minutos', 'Inicial desde $1.000', 'Autos con garantía e inspección completa'],
    proof1: '+1.200 clientes atendidos', proof2: 'en Massachusetts y la región',
    form_title: 'Simula tu financiamiento', form_sub: 'Gratis, rápido y sin compromiso.', step: 'PASO', of: 'DE',
    l_nome: 'Nombre completo', p_nome: 'Tu nombre y apellido', l_tel: 'Teléfono (con código de área)', l_email: 'Correo electrónico',
    l_fin: '¿Ya tienes algún vehículo financiado?', l_doc: '¿Qué documento tienes?',
    l_down: '¿Cuánto piensas dar de inicial?', p_down: 'O escribe otro valor ($)',
    l_when: '¿Cuándo piensas comprar el vehículo?', l_channel: '¿Cómo quieres recibir tu simulación ahora?',
    wa_desc: 'Recibe la simulación directo en tu WhatsApp', call_label: 'Llamada telefónica', call_desc: 'Un especialista te llama en español',
    privacy: 'Al continuar aceptas ser contactado por Speed Motors. Sin spam, sin consultas que afecten tu crédito.',
    next: 'Continuar', back: 'Volver', submit: 'Ver mi simulación ahora →',
    calc: ['Analizando tu perfil...', 'Consultando bancos aliados...', 'Preaprobando tu tasa especial...'], calc_wait: 'Espera solo un instante...',
    ok_title: '¡Tu preanálisis está listo! 🎉',
    ok_msg: (name, ch) => <>Recibimos tus datos, <b>{name}</b>. Un especialista de Speed Motors te contactará <b>en hasta 10 minutos</b> por <b style={{ color: NAVY }}>{ch}</b> con tu simulación personalizada.</>,
    protocol: 'Protocolo', entrada: 'Inicial', documento: 'Documento', ok_privacy: 'Tus datos están protegidos y no afectan tu score.',
    trust_title: '¿Por qué financiar con Speed Motors?',
    trust: [['Aceptamos tu documento', 'Pasaporte, ITIN o Social — trabajamos con bancos que aprueban a recién llegados.'], ['Respuesta en minutos', 'Nada de esperar días. Un especialista te responde en hasta 10 minutos en horario comercial.'], ['Atención en tu idioma', 'Equipo en Massachusetts que habla español y portugués. Entiendes cada detalle de tu contrato.']],
    how_title: 'Cómo funciona',
    how: [['Completa la simulación', 'Toma menos de 1 minuto — solo los datos esenciales.'], ['Recibe tu preanálisis', 'Nuestro equipo analiza y arma las mejores opciones para tu perfil.'], ['Elige tu auto', 'Un especialista te contacta por WhatsApp o teléfono y sales manejando.']],
    mid_cta: 'Quiero mi simulación gratis', testi_title: 'Quienes ya salieron manejando',
    testimonials: [['Marcos A. — Everett, MA', 'Llegué hace 8 meses, solo tenía pasaporte. En 2 días tenía mi Corolla financiado. Atención de 10.'], ['Juliana R. — Framingham, MA', 'Hice la simulación en Instagram y en 5 minutos me escribieron por WhatsApp. Súper transparentes con las cuotas.'], ['Pedro S. — Worcester, MA', 'Logré aprobar con ITIN e inicial de $2.000. Recomiendo mucho a Speed Motors.']],
    footer: 'Simulación gratuita y sin compromiso. El preanálisis no representa aprobación final de crédito, sujeta a evaluación de los bancos aliados. Ninguna consulta hecha en esta página afecta tu score.',
    sticky: 'Simular mi financiamiento gratis',
    e_nome: 'Escribe tu nombre completo.', e_tel: 'Escribe un teléfono válido.', e_email: 'Escribe un correo válido.',
    e_fin: 'Indica si ya tienes un vehículo financiado.', e_doc: 'Selecciona qué documento tienes.', e_down: 'Indica cuánto piensas dar de inicial.', e_when: 'Indica cuándo piensas comprar.', e_channel: 'Elige cómo quieres recibir tu simulación.', e_send: 'No se pudo enviar. Inténtalo de nuevo.',
    na_title: 'Página no disponible', na_sub: 'Esta página no existe o fue pausada.',
    opt: { 'Sim': 'Sí', 'Não': 'No', 'Passaporte': 'Pasaporte', 'ITIN': 'ITIN', 'Social': 'Social', 'Esta semana': 'Esta semana', 'Este mês': 'Este mes', 'Em 1–3 meses': 'En 1–3 meses', 'Só pesquisando': 'Solo investigando', [CH_WA]: 'WhatsApp', [CH_CALL]: 'Llamada telefónica' },
  },
};

const inputCls = 'w-full bg-white border border-slate-300 rounded-xl px-4 py-3.5 text-[15px] text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-[#27339B] focus:ring-2 focus:ring-[#27339B]/20';

const Seg = ({ options, value, onChange, testid, cols = 2, labels }) => (
  <div className={`grid gap-2 ${cols === 2 ? 'grid-cols-2' : cols === 3 ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`} data-testid={testid}>
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)} className="px-3 py-3 rounded-xl text-sm font-semibold border-2 transition-all" style={value === o ? { borderColor: NAVY, background: '#EEF1FA', color: NAVY } : { borderColor: '#E2E8F0', background: '#fff', color: '#475569' }}>
        {labels?.[o] || o}
      </button>
    ))}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">{label}</label>
    {children}
  </div>
);

export default function PublicLanding() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lang, setLang] = useState('pt');
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ full_name: '', phone: '', email: '', has_financed: '', down_payment: '', down_custom: '', document_type: '', purchase_when: '', contact_channel: '', website: '' });
  const [calcIdx, setCalcIdx] = useState(-1);
  const [ref, setRef] = useState('');
  const [error, setError] = useState('');
  const formRef = useRef(null);
  const t = T[lang];

  useEffect(() => {
    axios.get(`${API}/public/lp/${slug}`).then((r) => {
      if (!r.data.active) setNotFound(true); else setPage(r.data);
    }).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => { document.title = 'Speed Motors — Financiamento'; }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const set = (patch) => { setError(''); setF((prev) => ({ ...prev, ...patch })); };

  const next = () => {
    setError('');
    if (step === 1) {
      if (!f.full_name.trim() || f.full_name.trim().split(' ').length < 2) return setError(t.e_nome);
      if (f.phone.replace(/\D/g, '').length < 10) return setError(t.e_tel);
      if (!f.email.includes('@')) return setError(t.e_email);
    }
    if (step === 2) {
      if (!f.has_financed) return setError(t.e_fin);
      if (!f.document_type) return setError(t.e_doc);
      if (!f.down_payment && !f.down_custom) return setError(t.e_down);
      if (!f.purchase_when) return setError(t.e_when);
    }
    setStep(step + 1);
  };

  const submit = async () => {
    setError('');
    if (!f.contact_channel) return setError(t.e_channel);
    setCalcIdx(0);
    const t1 = setTimeout(() => setCalcIdx(1), 1100);
    const t2 = setTimeout(() => setCalcIdx(2), 2200);
    try {
      const payload = { ...f, down_payment: f.down_custom ? `$${f.down_custom}` : f.down_payment };
      const { data } = await axios.post(`${API}/public/lp/${slug}/submit`, payload);
      setTimeout(() => { setRef(data.ref); setCalcIdx(-1); }, 3300);
    } catch (e) {
      clearTimeout(t1); clearTimeout(t2);
      setCalcIdx(-1);
      setError(e.response?.data?.detail || t.e_send);
    }
  };

  const LangToggle = () => (
    <div className="flex items-center rounded-full border border-slate-200 bg-white p-0.5" data-testid="lp-lang-toggle">
      {['pt', 'es'].map((l) => (
        <button key={l} data-testid={`lp-lang-${l}`} onClick={() => setLang(l)} className="px-3 py-1.5 rounded-full text-xs font-black tracking-wide transition-all" style={lang === l ? { background: NAVY, color: '#fff' } : { color: '#64748B' }}>
          {l === 'pt' ? '🇧🇷 PT' : '🇪🇸 ES'}
        </button>
      ))}
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="text-center">
        <img src={LOGO} alt="Speed Motors" className="w-16 h-16 mx-auto mb-4" />
        <p className="font-black text-2xl text-slate-900">{T.pt.na_title}</p>
        <p className="text-slate-500 mt-2">{T.pt.na_sub}</p>
      </div>
    </div>
  );
  if (!page) return <div className="min-h-screen bg-slate-50" />;

  const progress = ref ? 100 : calcIdx >= 0 ? 90 : step === 1 ? 33 : step === 2 ? 66 : 85;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-20 sm:pb-0" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <img src={LOGO} alt="Speed Motors" className="w-10 h-10" data-testid="lp-logo" />
            <div className="leading-tight hidden xs:block sm:block">
              <p className="font-black text-sm tracking-widest" style={{ color: NAVY_DARK }}>SPEED</p>
              <p className="text-[9px] tracking-[0.3em] font-bold" style={{ color: NAVY_MID }}>MOTORS</p>
            </div>
          </div>
          <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border" style={{ color: NAVY, background: '#EEF1FA', borderColor: '#C9D2EE' }}><ShieldCheck className="w-3.5 h-3.5" /> {t.badge}</span>
          <div className="flex items-center gap-2">
            <LangToggle />
            <button data-testid="header-cta" onClick={scrollToForm} className="text-white text-sm font-bold px-5 py-2.5 rounded-full transition-opacity hover:opacity-90 shadow-lg" style={{ background: NAVY, boxShadow: '0 8px 24px rgba(27,38,104,0.3)' }}>{t.header_cta}</button>
          </div>
        </div>
      </header>

      {/* Hero + Form */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
        <div className="pt-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2" style={{ color: NAVY_MID }}><Zap className="w-4 h-4" /> {t.kicker}</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mt-4" style={{ color: NAVY_DARK }}>
            {t.title1}<span style={{ color: NAVY_MID }}>{t.title_hl}</span>
          </h1>
          <p className="text-lg text-slate-600 mt-5 leading-relaxed">{t.sub}</p>
          <ul className="mt-7 space-y-3">
            {t.bullets.map((b) => (
              <li key={b} className="flex items-center gap-3 text-[15px] text-slate-700"><CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: NAVY_MID }} /> {b}</li>
            ))}
          </ul>
          <div className="flex items-center gap-4 mt-8 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm w-fit">
            <img src={HERO_IMG} alt="Cliente feliz" className="w-16 h-16 rounded-xl object-cover" />
            <div>
              <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4" style={{ fill: NAVY_MID, color: NAVY_MID }} />)}</div>
              <p className="text-sm font-bold mt-1">{t.proof1}</p>
              <p className="text-xs text-slate-500">{t.proof2}</p>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div ref={formRef} className="bg-white border border-slate-200 rounded-3xl shadow-2xl p-6 sm:p-8 relative overflow-hidden" style={{ boxShadow: '0 24px 60px rgba(17,26,74,0.16)' }} data-testid="simulation-form-container">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-slate-100"><div className="h-full transition-all duration-500 rounded-r" style={{ width: `${progress}%`, background: NAVY_MID }} /></div>

          {ref ? (
            <div className="text-center py-6" data-testid="simulation-success-card">
              <div className="w-16 h-16 rounded-full border-2 flex items-center justify-center mx-auto" style={{ background: '#EEF1FA', borderColor: NAVY }}><CheckCircle2 className="w-8 h-8" style={{ color: NAVY }} /></div>
              <h2 className="text-2xl font-black mt-5" style={{ color: NAVY_DARK }}>{t.ok_title}</h2>
              <p className="text-slate-600 mt-3 leading-relaxed">{t.ok_msg(f.full_name.split(' ')[0], t.opt[f.contact_channel] || f.contact_channel)}</p>
              <div className="mt-5 bg-slate-50 border border-slate-200 rounded-xl p-4 text-left text-sm space-y-1.5">
                <p className="flex justify-between"><span className="text-slate-500">{t.protocol}</span><b className="font-mono">{ref}</b></p>
                <p className="flex justify-between"><span className="text-slate-500">{t.entrada}</span><b>{f.down_custom ? `$${f.down_custom}` : f.down_payment}</b></p>
                <p className="flex justify-between"><span className="text-slate-500">{t.documento}</span><b>{t.opt[f.document_type] || f.document_type}</b></p>
              </div>
              <p className="text-xs text-slate-400 mt-5 flex items-center justify-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {t.ok_privacy}</p>
            </div>
          ) : calcIdx >= 0 ? (
            <div className="text-center py-14" data-testid="simulation-calculating-loader">
              <div className="w-14 h-14 border-4 border-slate-200 rounded-full animate-spin mx-auto" style={{ borderTopColor: NAVY }} />
              <p className="text-lg font-bold mt-6" style={{ color: NAVY_DARK }}>{t.calc[calcIdx]}</p>
              <p className="text-sm text-slate-500 mt-2">{t.calc_wait}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-black" style={{ color: NAVY_DARK }}>{t.form_title}</h2>
                <span className="text-[11px] font-bold text-slate-400">{t.step} {step} {t.of} 3</span>
              </div>
              <p className="text-sm text-slate-500 mb-6">{t.form_sub}</p>

              <input type="text" name="website" value={f.website} onChange={(e) => set({ website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />

              {step === 1 && (
                <div className="space-y-4">
                  <Field label={t.l_nome}><input data-testid="simulation-input-nome" className={inputCls} value={f.full_name} onChange={(e) => set({ full_name: e.target.value })} placeholder={t.p_nome} /></Field>
                  <Field label={t.l_tel}><input data-testid="simulation-input-telefone" type="tel" className={inputCls} value={f.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="(617) 555-0100" /></Field>
                  <Field label={t.l_email}><input data-testid="simulation-input-email" type="email" className={inputCls} value={f.email} onChange={(e) => set({ email: e.target.value })} placeholder="voce@email.com" /></Field>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <Field label={t.l_fin}><Seg options={FIN_OPTIONS} labels={t.opt} value={f.has_financed} onChange={(v) => set({ has_financed: v })} testid="simulation-veiculo-financiado" /></Field>
                  <Field label={t.l_doc}><Seg options={DOC_OPTIONS} labels={t.opt} value={f.document_type} onChange={(v) => set({ document_type: v })} testid="simulation-select-documento" cols={3} /></Field>
                  <Field label={t.l_down}>
                    <Seg options={DOWN_OPTIONS} value={f.down_payment} onChange={(v) => set({ down_payment: v, down_custom: '' })} testid="simulation-slider-entrada" cols={4} />
                    <input className={`${inputCls} mt-2`} value={f.down_custom} onChange={(e) => set({ down_custom: e.target.value.replace(/[^\d]/g, ''), down_payment: '' })} placeholder={t.p_down} />
                  </Field>
                  <Field label={t.l_when}><Seg options={WHEN_OPTIONS} labels={t.opt} value={f.purchase_when} onChange={(v) => set({ purchase_when: v })} testid="simulation-select-prazo" /></Field>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5">
                  <Field label={t.l_channel}>
                    <div className="grid grid-cols-1 gap-3" data-testid="simulation-canal-resposta">
                      <button type="button" onClick={() => set({ contact_channel: CH_WA })} className="flex items-center gap-3 px-4 py-4 rounded-xl border-2 transition-all text-left" style={f.contact_channel === CH_WA ? { borderColor: NAVY, background: '#EEF1FA' } : { borderColor: '#E2E8F0', background: '#fff' }}>
                        <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: NAVY }}><MessageCircle className="w-5.5 h-5.5 text-white" /></div>
                        <div><p className="font-bold">WhatsApp</p><p className="text-xs text-slate-500">{t.wa_desc}</p></div>
                      </button>
                      <button type="button" onClick={() => set({ contact_channel: CH_CALL })} className="flex items-center gap-3 px-4 py-4 rounded-xl border-2 transition-all text-left" style={f.contact_channel === CH_CALL ? { borderColor: NAVY, background: '#EEF1FA' } : { borderColor: '#E2E8F0', background: '#fff' }}>
                        <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: NAVY_MID }}><Phone className="w-5 h-5 text-white" /></div>
                        <div><p className="font-bold">{t.call_label}</p><p className="text-xs text-slate-500">{t.call_desc}</p></div>
                      </button>
                    </div>
                  </Field>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 shrink-0" /> {t.privacy}</p>
                </div>
              )}

              {error && <p data-testid="simulation-error" className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-4">{error}</p>}

              <div className="flex gap-3 mt-6">
                {step > 1 && <button type="button" data-testid="simulation-back-button" onClick={() => { setError(''); setStep(step - 1); }} className="flex items-center gap-1 px-4 py-3.5 rounded-xl border-2 border-slate-200 text-slate-600 font-bold text-sm hover:border-slate-300 transition-colors"><ChevronLeft className="w-4 h-4" /> {t.back}</button>}
                {step < 3 ? (
                  <button type="button" data-testid="simulation-next-step-button" onClick={next} className="flex-1 flex items-center justify-center gap-1.5 text-white font-black text-[15px] px-5 py-3.5 rounded-xl transition-opacity hover:opacity-90 shadow-lg" style={{ background: NAVY, boxShadow: '0 10px 26px rgba(27,38,104,0.32)' }}>{t.next} <ChevronRight className="w-4 h-4" /></button>
                ) : (
                  <button type="button" data-testid="simulation-submit-button" onClick={submit} className="flex-1 text-white font-black text-[15px] px-5 py-3.5 rounded-xl transition-opacity hover:opacity-90 shadow-lg animate-pulse" style={{ background: NAVY, boxShadow: '0 10px 26px rgba(27,38,104,0.32)' }}>{t.submit}</button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Trust bento */}
      <section className="bg-white border-y border-slate-200 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center" style={{ color: NAVY_DARK }}>{t.trust_title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {t.trust.map(([title, desc], i) => {
              const Icon = [FileCheck2, Clock, ShieldCheck][i];
              return (
                <div key={title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: NAVY }}><Icon className="w-5.5 h-5.5 text-white" /></div>
                  <h3 className="font-black text-lg mt-4" style={{ color: NAVY_DARK }}>{title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center" style={{ color: NAVY_DARK }}>{t.how_title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
          {t.how.map(([title, desc], i) => (
            <div key={title} className="text-center px-4">
              <div className="w-12 h-12 rounded-full text-white font-black text-lg flex items-center justify-center mx-auto" style={{ background: NAVY }}>{i + 1}</div>
              <h3 className="font-black text-lg mt-4" style={{ color: NAVY_DARK }}>{title}</h3>
              <p className="text-sm text-slate-600 mt-2">{desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <button data-testid="mid-cta" onClick={scrollToForm} className="text-white font-black px-8 py-4 rounded-full transition-opacity hover:opacity-90 shadow-xl" style={{ background: NAVY, boxShadow: '0 14px 34px rgba(27,38,104,0.35)' }}>{t.mid_cta}</button>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 text-white" style={{ background: NAVY_DARK }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-center">{t.testi_title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {t.testimonials.map(([who, quote]) => (
              <div key={who} className="rounded-2xl p-6 border" style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.14)' }}>
                <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-white text-white" />)}</div>
                <p className="text-sm mt-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.82)' }}>“{quote}”</p>
                <p className="text-xs font-bold mt-4 text-white">{who}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ background: '#0B123A', color: 'rgba(255,255,255,0.6)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center space-y-4">
          <img src={LOGO} alt="Speed Motors" className="w-14 h-14 mx-auto" />
          <p className="font-black text-white tracking-widest">SPEED MOTORS</p>
          <p className="text-xs max-w-xl mx-auto leading-relaxed">{t.footer}</p>
          <p className="text-[11px]">© {new Date().getFullYear()} Speed Motors · Massachusetts, USA</p>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      {!ref && calcIdx < 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 p-3 bg-white/95 backdrop-blur border-t border-slate-200 sm:hidden">
          <button data-testid="sticky-mobile-cta" onClick={scrollToForm} className="w-full text-white font-black py-3.5 rounded-xl shadow-lg" style={{ background: NAVY }}>{t.sticky}</button>
        </div>
      )}
    </div>
  );
}
